import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {listUnreadMessages} from '../../../service/ChatService';
import {createRefreshQueue} from '../refreshQueue';

export default function useChannelUnread(conversationId, revision, connectionStatus) {
    const [state, setState] = useState({items: [], count: null, next: null, loading: false, error: null});
    const request = useRef(null);
    const cursor = useRef(null);
    const queue = useMemo(() => createRefreshQueue(async (more, signal) => {
        if (!conversationId || (more && !cursor.current)) return;
        request.current = signal;
        const beforeSequence = more ? cursor.current : undefined;
        setState(current => ({...current, loading: true, error: null}));
        try {
            const payload = await listUnreadMessages(conversationId, {beforeSequence, signal});
            if (signal.aborted) return;
            cursor.current = payload.next_before_sequence;
            setState(current => ({
                items: more ? [...new Map([...current.items, ...payload.items].map(item => [item.id, item])).values()] : payload.items,
                count: payload.unread_count, next: payload.next_before_sequence, loading: false, error: null,
            }));
        } catch (error) {
            if (!signal.aborted) setState(current => ({...current, loading: false, error}));
        } finally {
            if (request.current === signal) request.current = null;
        }
    }, (left, right) => left && right, {minIntervalMs: 1000}), [conversationId]);
    const load = useCallback((more = false) => {
        if (!conversationId || (more && (!cursor.current || request.current))) return Promise.resolve();
        return queue.request(Boolean(more)).catch(error => {
            if (error?.name !== 'AbortError') setState(current => ({...current, loading: false, error}));
        });
    }, [conversationId, queue]);
    const refresh = useCallback(() => load(false), [load]);
    const loadMore = useCallback(() => load(true), [load]);
    useEffect(() => {
        cursor.current = null;
        setState({items: [], count: null, next: null, loading: false, error: null});
        return () => { queue.cancel(); request.current = null; };
    }, [queue]);
    useEffect(() => {
        // Coalesce events without aborting useful SQL work. Hidden tabs catch up
        // on visibility change; a busy channel starts at most one request/sec.
        // The queue owns the timer, so continuous events cannot postpone a
        // refresh indefinitely by restarting a trailing debounce.
        if (document.visibilityState === 'visible') void refresh();
    }, [refresh, revision, connectionStatus]);
    useEffect(() => {
        const visible = () => { if (document.visibilityState === 'visible') void refresh(); };
        window.addEventListener('focus', visible);
        window.addEventListener('chat-refresh-read', refresh);
        document.addEventListener('visibilitychange', visible);
        return () => { window.removeEventListener('focus', visible); window.removeEventListener('chat-refresh-read', refresh); document.removeEventListener('visibilitychange', visible); };
    }, [refresh]);
    return {...state, refresh, loadMore};
}
